import type React from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { RiFile2Line } from '@itandi/itandi-bb-ui';
import Styles from './BillingCreateNavigation.module.scss';
import { toThousandSeparator } from '~/utils/CurrencySeparator';
import { Button } from '~/components/common/parts/button/Button';
import { create as generateBillings } from '~/utils/api/deals/DealBillingGenerate';
import { isSuccess } from '~/utils/api/api_handler';
import { PATH as OWNER_BILLINGS_PATH } from '~/pages/deals/[id]/billings/edit';
import { PATH as RENTEE_BILLINGS_PATH } from '~/pages/deals/[id]/billing_rentees/edit';
import type { DealEstimationContext } from '~/models/DealEstimate';
import type { DealBillingContext } from '~/models/DealBilling';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { updateAccept } from '~/utils/api/deals/DealEstimationHeader';
import { isOwnStatus } from '~/models/Status';
import type { Deal } from '~/models/Deal';

type Props = {
  title: string;
  deal: Deal;
  estimationContext: DealEstimationContext;
  ownerBillingContext: DealBillingContext;
  renteeBillingContext: DealBillingContext;
  mutateBillings: () => Promise<void>;
};
/**
 *
 * @param title
 * @param dealId
 * @param estimationContext
 * @param ownerBillingContext
 * @param renteeBillingContext
 * @constructor
 * @for client
 */
export const BillingCreateNavigation: React.FC<Props> = ({
  title,
  deal,
  estimationContext,
  ownerBillingContext,
  renteeBillingContext,
  mutateBillings
}: Props) => {
  const router = useRouter();
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(deal.id);

  if (
    dealEstimationHistoryContext === null ||
    (estimationContext.dealConstructions.length === 0 &&
      dealEstimationHistoryContext.dealConstructions.length === 0)
  ) {
    return null;
  }
  const estimationHeaderId =
    dealEstimationHistoryContext.dealEstimationHeaderId;
  if (
    ownerBillingContext.dealConstructions.length > 0 ||
    renteeBillingContext.dealConstructions.length > 0
  ) {
    const { sumOwnerAmountWithTax } = ownerBillingContext;
    const { sumRenteeAmountWithTax } = renteeBillingContext;
    return (
      <div className={Styles.NavigationBlock}>
        <div className={Styles.IconWrapper}>
          <RiFile2Line className={Styles.Icon} />
        </div>
        <div className={Styles.NavigationContentList}>
          <h4 className={Styles.Title}>{title}</h4>
          <div className={Styles.BillingRow}>
            <div className={Styles.BillingRowTitle}>
              オーナーへの請求書
              <div className={Styles.BillingRowSubText}>
                入力中のオーナーへの請求額：¥
                {toThousandSeparator(sumOwnerAmountWithTax)}（税込）
              </div>
            </div>
            <div className={Styles.DetailLinkBlock}>
              <Button
                className={Styles.DetailLinkButton}
                onClick={(): void => {
                  router.push(OWNER_BILLINGS_PATH(deal.id)).then();
                }}
                size="lg"
                variant="normal"
              >
                請求書明細画面
              </Button>
            </div>
          </div>
          <div className={Styles.BillingRow}>
            <div className={Styles.BillingRowTitle}>
              入居者への請求書
              <div className={Styles.BillingRowSubText}>
                入力中の入居者への請求額：¥
                {toThousandSeparator(sumRenteeAmountWithTax)}（税込）
              </div>
            </div>
            <div className={Styles.DetailLinkBlock}>
              <Button
                className={Styles.DetailLinkButton}
                onClick={(): void => {
                  router.push(RENTEE_BILLINGS_PATH(deal.id)).then();
                }}
                size="lg"
                variant="normal"
              >
                請求書明細画面
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onClickCreateBillings = async (): Promise<void> => {
    // 非共有案件で申請前(draftのheader)が存在する場合は承認させる
    // https://itandi-dev.atlassian.net/browse/NSKDEV-1622対応
    if (isOwnStatus(deal.status) && estimationHeaderId !== null) {
      const response = await updateAccept(deal.id, estimationHeaderId);
      if (!isSuccess(response)) {
        toast.error('エラーが発生しました');
        return;
      }
    }
    const result = await generateBillings(deal.id, null);
    if (isSuccess(result)) {
      await mutateBillings();
      toast.success('請求を作成しました');
      return;
    }
    toast.error('エラーが発生しました');
  };
  return (
    <div className={Styles.SuggestBlock}>
      <div className={Styles.IconWrapper}>
        <RiFile2Line className={Styles.Icon} />
      </div>
      <div className={Styles.NavigationContent}>
        <div>
          <h4 className={Styles.Title}>{title}</h4>
        </div>
        <div className={Styles.DetailLinkBlock}>
          <Button onClick={onClickCreateBillings} size="lg" variant="normal">
            工事見積りから請求を作成する
          </Button>
        </div>
      </div>
    </div>
  );
};
